import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../lib/api";
import { useUnsavedGuard } from "./unsavedGuard";
import { SavedNote, XIcon } from "../shared/Icons";
import { toastError } from "../shared/toast";
import { ConfirmDialog } from "../common/ConfirmDialog";

// Subtítulo de seção do formulário.
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)", borderBottom: "1px solid var(--line-soft)", paddingBottom: 6, marginTop: 8 }}>
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
};

const DOW = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

type OperatingWindow = { days: number[]; start: string; end: string };

// Whitelist por canal (cada canal tem a sua; ligar num não trava os outros).
const WHITELISTS = [
  { key: "whitelist", en: "whitelist_enabled", label: "WhatsApp", ph: "5511999999999\n5521888888888", hint: "Um número por linha." },
  { key: "whitelist_instagram", en: "whitelist_instagram_enabled", label: "Instagram", ph: "@usuario ou 17841400000000000", hint: "@username ou o id do Instagram." },
  { key: "whitelist_messenger", en: "whitelist_messenger_enabled", label: "Messenger", ph: "1234567890123456", hint: "Um id (PSID) por linha." },
] as const;
const toList = (t: string) => t.split("\n").map((l) => l.trim()).filter(Boolean);

export function ConfigAvancadas({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const adv = agent.advanced ?? {};
  const oh = adv.operating_hours ?? {};

  const [messageWait, setMessageWait] = useState<string>(String(adv.message_wait_seconds ?? "30"));
  const [maxMessages, setMaxMessages] = useState<string>(adv.max_messages_per_client != null ? String(adv.max_messages_per_client) : "");
  const [ctxLimit, setCtxLimit] = useState<string>(String(adv.context_messages_limit ?? "50"));
  const [humanTtl, setHumanTtl] = useState<string>(adv.human_block_ttl_seconds ? String(adv.human_block_ttl_seconds) : "");
  const [qualifiedTtl, setQualifiedTtl] = useState<string>(String(adv.qualified_ttl_days ?? "30"));
  const [blacklist, setBlacklist] = useState<string>(Array.isArray(adv.blacklist) ? adv.blacklist.join("\n") : "");
  const [wls, setWls] = useState<Record<string, { on: boolean; text: string }>>(() =>
    Object.fromEntries(WHITELISTS.map((w) => [w.key, {
      on: adv[w.en] ?? false,
      text: Array.isArray(adv[w.key]) ? adv[w.key].join("\n") : "",
    }])));
  const setWl = (key: string, patch: Partial<{ on: boolean; text: string }>) =>
    setWls((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const [webSearch, setWebSearch] = useState<boolean>(adv.web_search_enabled ?? false);
  // Motor da busca (teste em paralelo): native = SearXNG, deepseek = DeepSeek Search+Fetch.
  const [webEngine, setWebEngine] = useState<"native" | "deepseek">(adv.web_search_engine === "deepseek" ? "deepseek" : "native");
  const [ohEnabled, setOhEnabled] = useState<boolean>(oh.enabled ?? false);
  const [ohWindows, setOhWindows] = useState<OperatingWindow[]>(
    Array.isArray(oh.windows) && oh.windows.length > 0
      ? oh.windows
      : [{ days: [0, 1, 2, 3, 4], start: "08:00", end: "18:00" }],
  );

  const setWin = (i: number, patch: Partial<OperatingWindow>) =>
    setOhWindows(ohWindows.map((w, j) => (j === i ? { ...w, ...patch } : w)));

  const current = JSON.stringify({ messageWait, maxMessages, ctxLimit, humanTtl, qualifiedTtl, blacklist, wls, ohEnabled, ohWindows, webSearch, webEngine });
  const [savedSnap, setSavedSnap] = useState(current);

  // TTL da trava humana mudou → 2 confirmações antes de salvar (o backend
  // refresca todas as travas ativas para o novo valor).
  const ttlChanged = (humanTtl.trim() ? parseInt(humanTtl) : 0) !== (adv.human_block_ttl_seconds || 0);
  const [ttlConfirm, setTtlConfirm] = useState<0 | 1 | 2>(0);
  const save = () => (ttlChanged ? setTtlConfirm(1) : mut.mutate());

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/agents/${agent.id}`, {
        advanced: {
          ...(agent.advanced ?? {}),
          message_wait_seconds: parseInt(messageWait) || 30,
          max_messages_per_client: maxMessages.trim() ? parseInt(maxMessages) : null,
          context_messages_limit: parseInt(ctxLimit) || 50,
          human_block_ttl_seconds: humanTtl.trim() ? parseInt(humanTtl) : 0,
          qualified_ttl_days: qualifiedTtl.trim() ? parseInt(qualifiedTtl) : 30,
          blacklist: toList(blacklist),
          ...Object.fromEntries(WHITELISTS.flatMap((w) => [[w.key, toList(wls[w.key].text)], [w.en, wls[w.key].on]])),
          operating_hours: { enabled: ohEnabled, windows: ohWindows },
          web_search_enabled: webSearch,
          web_search_engine: webEngine,
        },
      }),
    onSuccess: () => {
      setSavedSnap(current);
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
    },
    onError: (err) => toastError((err as any)?.payload?.detail ?? "Falha ao salvar configurações avançadas."),
  });

  useUnsavedGuard(current !== savedSnap, mut.mutateAsync);

  return (
    <div style={{ maxWidth: 480, display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionTitle>Acesso à internet</SectionTitle>
      <div>
        <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 4 }}>
          <input type="checkbox" checked={webSearch} onChange={(e) => setWebSearch(e.target.checked)} style={{ accentColor: "var(--brand)" }} />
          Permitir que o agente pesquise na internet
        </label>
        <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
          O agente ganha a ferramenta de busca pra checar o que não sabe (preços, notícias, regras). Vale ao publicar.
        </div>
        {webSearch && (
          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>Motor de busca</label>
            <select style={inputStyle} value={webEngine} onChange={(e) => setWebEngine(e.target.value as "native" | "deepseek")}>
              <option value="native">Nativo</option>
              <option value="deepseek">DeepSeek</option>
            </select>
          </div>
        )}
      </div>
      {ttlConfirm === 1 && (
        <ConfirmDialog
          title="Alterar o TTL da trava humana?"
          message="Tem certeza que deseja salvar um novo tempo de expiração da trava de atendimento humano?"
          confirmLabel="Sim, continuar"
          onConfirm={() => setTtlConfirm(2)}
          onCancel={() => setTtlConfirm(0)}
        />
      )}
      {ttlConfirm === 2 && (
        <ConfirmDialog
          title="Isso afeta as travas ativas"
          message={`Todas as conversas atualmente em atendimento humano deste agente passam a expirar em ${humanTtl.trim() ? `${humanTtl}s` : "nunca (só com #reset)"}. Conversas podem voltar para a IA antes do esperado.`}
          confirmLabel="Salvar mesmo assim"
          danger
          onConfirm={() => { setTtlConfirm(0); mut.mutate(); }}
          onCancel={() => setTtlConfirm(0)}
        />
      )}
      <SectionTitle>Fluxo de mensagens</SectionTitle>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Aguardar mensagens (segundos)</label>
        <input
          type="number"
          style={inputStyle}
          value={messageWait}
          onChange={(e) => setMessageWait(e.target.value)}
          min={0}
        />
      </div>

      <SectionTitle>Memória e contexto</SectionTitle>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>TTL de lead qualificado (dias)</label>
        <input
          type="number"
          style={inputStyle}
          value={qualifiedTtl}
          onChange={(e) => setQualifiedTtl(e.target.value)}
          min={0}
        />
        <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>
          Uma tool estratégica só requalifica a mesma entidade após esse período. 0 = sem TTL.
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Interações no contexto (histórico enviado ao agente)</label>
        <input
          type="number"
          style={inputStyle}
          value={ctxLimit}
          onChange={(e) => setCtxLimit(e.target.value)}
          min={1}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Expiração do bloqueio humano, em segundos (vazio = sem expiração)</label>
        <input
          type="number"
          style={inputStyle}
          value={humanTtl}
          onChange={(e) => setHumanTtl(e.target.value)}
          placeholder="Sem expiração"
          min={0}
        />
      </div>

      <SectionTitle>Limites</SectionTitle>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Máx. mensagens por cliente (vazio = ilimitado)</label>
        <input
          type="number"
          style={inputStyle}
          value={maxMessages}
          onChange={(e) => setMaxMessages(e.target.value)}
          placeholder="Sem limite"
          min={0}
        />
        <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 6 }}>
          Conta envios da IA (mensagem picotada = 1 envio; inclui follow-ups). Mensagens do cliente não contam.
        </span>
      </div>

      <SectionTitle>Horário de funcionamento</SectionTitle>

      {/* ── Horário de funcionamento — agente atende só FORA das janelas ── */}
      <div style={{ border: "1px solid var(--line-strong)", borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input type="checkbox" id="oh-enabled" checked={ohEnabled}
            onChange={(e) => setOhEnabled(e.target.checked)}
            style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
          <label htmlFor="oh-enabled" style={{ fontSize: 13, color: "var(--ink-secondary)", cursor: "pointer" }}>
            Horário de funcionamento da loja (o agente atende só fora dele)
          </label>
        </div>
        {ohEnabled && (
          <>
            {ohWindows.map((w, i) => (
              <div key={i} style={{ border: "1px solid var(--line)", borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  {DOW.map((d, di) => {
                    const on = w.days.includes(di);
                    return (
                      <button key={di} type="button"
                        onClick={() => setWin(i, { days: on ? w.days.filter((x) => x !== di) : [...w.days, di].sort((a, b) => a - b) })}
                        style={{
                          fontSize: 12, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                          border: `1px solid ${on ? "var(--brand)" : "var(--line-strong)"}`,
                          background: on ? "rgba(195,32,32,0.12)" : "var(--surface-hover)",
                          color: on ? "var(--brand)" : "var(--ink-soft)",
                        }}>
                        {d}
                      </button>
                    );
                  })}
                  {ohWindows.length > 1 && (
                    <button type="button" onClick={() => setOhWindows(ohWindows.filter((_, j) => j !== i))}
                      style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "var(--ink-muted)" }}
                      title="Remover janela">
                      <XIcon size={13} />
                    </button>
                  )}
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <label style={labelStyle}>Abre</label>
                    <input type="time" style={inputStyle} value={w.start}
                      onChange={(e) => setWin(i, { start: e.target.value })} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <label style={labelStyle}>Fecha</label>
                    <input type="time" style={inputStyle} value={w.end}
                      onChange={(e) => setWin(i, { end: e.target.value })} />
                  </div>
                </div>
              </div>
            ))}
            <button type="button"
              onClick={() => setOhWindows([...ohWindows, { days: [], start: "08:00", end: "13:00" }])}
              style={{ alignSelf: "flex-start", fontSize: 12, padding: "5px 12px", borderRadius: 6, cursor: "pointer", border: "1px solid var(--line-strong)", background: "var(--surface-hover)", color: "var(--ink-secondary)" }}>
              + Adicionar janela
            </button>
            <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
              Janelas separadas p/ horários diferentes (ex: seg–sex 8–19, sáb 8–13). Dia fora de
              todas as janelas = loja fechada = agente atende o dia todo. Mensagens recebidas
              dentro do horário entram no histórico como contexto, sem resposta do agente.
            </span>
          </>
        )}
      </div>

      <SectionTitle>Listas</SectionTitle>

      <div style={{ display: "flex", flexDirection: "column", maxWidth: 360 }}>
        <label style={labelStyle}>Blacklist (um número por linha, todos os canais)</label>
        <textarea
          value={blacklist}
          onChange={(e) => setBlacklist(e.target.value)}
          placeholder={"5511999999999\n5521888888888"}
          style={{
            ...inputStyle,
            height: 120,
            resize: "vertical",
            fontFamily: "var(--font-mono)",
            lineHeight: 1.5,
          }}
        />
      </div>

      {/* Whitelist por canal — ligar num canal não trava os outros */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {WHITELISTS.map((w) => (
          <div key={w.key} style={{ display: "flex", flexDirection: "column", flex: "1 1 220px", minWidth: 0 }}>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={wls[w.key].on} onChange={(e) => setWl(w.key, { on: e.target.checked })} style={{ accentColor: "var(--brand)" }} />
              Whitelist {w.label}
            </label>
            <textarea
              value={wls[w.key].text}
              onChange={(e) => setWl(w.key, { text: e.target.value })}
              placeholder={w.ph}
              style={{
                ...inputStyle,
                height: 120,
                resize: "vertical",
                fontFamily: "var(--font-mono)",
                lineHeight: 1.5,
              }}
            />
            <span style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 6, lineHeight: 1.5 }}>
              {w.hint} Quem manda #cadastro pelo canal entra sozinho.
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={mut.isPending}
        style={{
          background: "var(--brand)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "8px 20px",
          fontSize: 13,
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        {mut.isPending ? "Salvando..." : "Salvar"}
      </button>
      <SavedNote show={mut.isSuccess} />
    </div>
  );
}
